import React, { useEffect, useState } from 'react';
import DocumentUpload from '../../components/DocumentUpload';
import LocationSelectors from '../../components/LocationSelectors';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/FormPages.css';

const BailFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;

  const [formData, setFormData] = useState({
    country: "",
    state: "",
    district: "",

    // Step 1: Applicant Information
    applicantFullName: '',
    applicantFatherOrHusband: '',
    applicantAge: '',
    applicantGender: '',
    applicantOccupation: '',
    applicantContact: '',
    applicantEmail: '',
    applicantPresentAddress: '',
    applicantPermanentAddress: '',
    applicantRelationshipWithAccused: '',
    applicantRelationshipOther: '',

    // Step 2: Accused Person Details
    accusedFullName: '',
    accusedFatherOrHusband: '',
    accusedAge: '',
    accusedGender: '',
    accusedOccupation: '',
    accusedAddress: '',

    // Step 3: Case Details
    firNumber: '',
    policeStation: '',
    firDate: '',
    sectionOfLaw: '',
    caseDetails: '',
    chargesDescription: '',
    arrestDate: '',
    arrestingOfficer: '',
    custodyType: 'Police Custody',

    // Step 4: Bail Grounds
    bailGrounds: '',
    previousBailApplications: 'No',
    suretyDetails: '',
    financialCapacity: '',
    rootsInCommunity: '',
    likelihoodToFlee: 'Low',
    interferenceWithWitnesses: 'No',

    // Step 5: Legal Arguments & Declaration
    legalArguments: '',
    caseReferences: '',
    advocateName: '',
    advocateEnrollment: '',
    declaration: false
  });

  useEffect(() => {
    const storedCountry = localStorage.getItem("selectedCountry") || "";
    const storedState = localStorage.getItem("selectedState") || "";
    const storedDistrict = localStorage.getItem("selectedDistrict") || "";

    const initialCountry = location.state?.country || storedCountry;
    const initialState = location.state?.state || storedState;
    const initialDistrict = location.state?.district || storedDistrict;

    setFormData((prev) => ({
      ...prev,
      country: prev.country || initialCountry,
      state: prev.state || initialState,
      district: prev.district || initialDistrict,
    }));
  }, [location.state]);

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const country = formData.country || localStorage.getItem("selectedCountry") || "";
    const stateVal = formData.state || localStorage.getItem("selectedState") || "";
    const district = formData.district || localStorage.getItem("selectedDistrict") || "";
    const lawCategory = "Criminal";

    let detailsText = `
👤 APPLICANT DETAILS:
Full Name: ${formData.applicantFullName}
Father's/Husband's Name: ${formData.applicantFatherOrHusband}
Age: ${formData.applicantAge}
Gender: ${formData.applicantGender}
Occupation: ${formData.applicantOccupation}
Contact: ${formData.applicantContact}
Email: ${formData.applicantEmail}
Present Address: ${formData.applicantPresentAddress}
Permanent Address: ${formData.applicantPermanentAddress}
Relationship with Accused: ${formData.applicantRelationshipOther || formData.applicantRelationshipWithAccused}

👤 ACCUSED PERSON DETAILS:
Full Name: ${formData.accusedFullName}
Father's/Husband's Name: ${formData.accusedFatherOrHusband}
Age: ${formData.accusedAge}
Gender: ${formData.accusedGender}
Occupation: ${formData.accusedOccupation}
Address: ${formData.accusedAddress}

📋 CASE DETAILS:
FIR Number: ${formData.firNumber}
Police Station: ${formData.policeStation}
FIR Date: ${formData.firDate}
Sections of Law: ${formData.sectionOfLaw}
Case Details: ${formData.caseDetails}
Charges Description: ${formData.chargesDescription}
Date of Arrest: ${formData.arrestDate}
Arresting Officer: ${formData.arrestingOfficer}
Custody Type: ${formData.custodyType}

⚖️ BAIL GROUNDS:
Grounds for Bail: ${formData.bailGrounds}
Previous Bail Applications: ${formData.previousBailApplications}
Surety Details: ${formData.suretyDetails}
Financial Capacity: ${formData.financialCapacity}
Roots in Community: ${formData.rootsInCommunity}
Likelihood to Flee: ${formData.likelihoodToFlee}
Interference with Witnesses: ${formData.interferenceWithWitnesses}

📝 LEGAL ARGUMENTS:
Legal Arguments: ${formData.legalArguments}
Case References: ${formData.caseReferences}
Advocate Name: ${formData.advocateName}
Advocate Enrollment: ${formData.advocateEnrollment}
`.trim();

    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:`;
      uploadedDocuments.forEach((doc, idx) => {
        detailsText += `\n\nDocument ${idx + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country,
        state: stateVal,
        district,
        lawCategory,
  draftType: "bail",
  entityName: formData.applicantFullName,
  uploadedDocuments,
  extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Applicant Information
      </div>
      
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="applicantFullName"
            value={formData.applicantFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="applicantFatherOrHusband"
            value={formData.applicantFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="applicantAge"
            value={formData.applicantAge}
            onChange={handleInputChange}
            min="18"
            max="120"
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="applicantGender"
            value={formData.applicantGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation</label>
          <input
            type="text"
            name="applicantOccupation"
            value={formData.applicantOccupation}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="applicantContact"
            value={formData.applicantContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Email Address</label>
          <input
            type="email"
            name="applicantEmail"
            value={formData.applicantEmail}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Present Address *</label>
          <textarea
            name="applicantPresentAddress"
            value={formData.applicantPresentAddress}
            onChange={handleInputChange}
            rows="2"
            required
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Permanent Address</label>
          <textarea
            name="applicantPermanentAddress"
            value={formData.applicantPermanentAddress}
            onChange={handleInputChange}
            rows="2"
          />
        </div>
        <div className="form-page-col">
          <label>Relationship with Accused *</label>
          <select
            name="applicantRelationshipWithAccused"
            value={formData.applicantRelationshipWithAccused}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Relationship</option>
            <option value="Self">Self (I am the accused)</option>
            <option value="Father">Father</option>
            <option value="Mother">Mother</option>
            <option value="Spouse">Spouse</option>
            <option value="Brother">Brother</option>
            <option value="Sister">Sister</option>
            <option value="Son">Son</option>
            <option value="Daughter">Daughter</option>
            <option value="Friend">Friend</option>
            <option value="Advocate">Advocate</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />

      {formData.applicantRelationshipWithAccused === 'Other' && (
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Specify Relationship</label>
            <input
              type="text"
              name="applicantRelationshipOther"
              value={formData.applicantRelationshipOther}
              onChange={handleInputChange}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Accused Person Details
      </div>
      
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="accusedFullName"
            value={formData.accusedFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="accusedFatherOrHusband"
            value={formData.accusedFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="accusedAge"
            value={formData.accusedAge}
            onChange={handleInputChange}
            min="18"
            max="120"
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="accusedGender"
            value={formData.accusedGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation</label>
          <input
            type="text"
            name="accusedOccupation"
            value={formData.accusedOccupation}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Address *</label>
          <textarea
            name="accusedAddress"
            value={formData.accusedAddress}
            onChange={handleInputChange}
            rows="2"
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📋 Case Details
      </div>
      
      <div className="form-page-row">
        <div className="form-page-col">
          <label>FIR Number *</label>
          <input
            type="text"
            name="firNumber"
            value={formData.firNumber}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Police Station *</label>
          <input
            type="text"
            name="policeStation"
            value={formData.policeStation}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>FIR Date *</label>
          <input
            type="date"
            name="firDate"
            value={formData.firDate}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Sections of Law *</label>
          <input
            type="text"
            name="sectionOfLaw"
            value={formData.sectionOfLaw}
            onChange={handleInputChange}
            placeholder="e.g., Section 420, 406 IPC"
            required
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Case Details *</label>
          <textarea
            name="caseDetails"
            value={formData.caseDetails}
            onChange={handleInputChange}
            rows="3"
            placeholder="Brief description of the case"
            required
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Charges Description *</label>
          <textarea
            name="chargesDescription"
            value={formData.chargesDescription}
            onChange={handleInputChange}
            rows="3"
            placeholder="Detailed description of charges against the accused"
            required
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Date of Arrest</label>
          <input
            type="date"
            name="arrestDate"
            value={formData.arrestDate}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Arresting Officer</label>
          <input
            type="text"
            name="arrestingOfficer"
            value={formData.arrestingOfficer}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Custody Type *</label>
          <select
            name="custodyType"
            value={formData.custodyType}
            onChange={handleInputChange}
            required
          >
            <option value="Police Custody">Police Custody</option>
            <option value="Judicial Custody">Judicial Custody</option>
            <option value="Remand">Remand</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        ⚖️ Bail Grounds & Arguments
      </div>
      
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Grounds for Bail *</label>
          <textarea
            name="bailGrounds"
            value={formData.bailGrounds}
            onChange={handleInputChange}
            rows="4"
            placeholder="State the grounds on which bail is being sought (e.g., nature of offense, no flight risk, etc.)"
            required
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Previous Bail Applications *</label>
          <select
            name="previousBailApplications"
            value={formData.previousBailApplications}
            onChange={handleInputChange}
            required
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </div>
        <div className="form-page-col">
          <label>Likelihood to Flee *</label>
          <select
            name="likelihoodToFlee"
            value={formData.likelihoodToFlee}
            onChange={handleInputChange}
            required
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Surety Details</label>
          <textarea
            name="suretyDetails"
            value={formData.suretyDetails}
            onChange={handleInputChange}
            rows="3"
            placeholder="Details of surety (name, address, occupation, income)"
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Financial Capacity</label>
          <textarea
            name="financialCapacity"
            value={formData.financialCapacity}
            onChange={handleInputChange}
            rows="3"
            placeholder="Financial capacity to provide bail bond"
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Roots in Community</label>
          <textarea
            name="rootsInCommunity"
            value={formData.rootsInCommunity}
            onChange={handleInputChange}
            rows="3"
            placeholder="Evidence of strong roots in the community"
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Interference with Witnesses *</label>
          <select
            name="interferenceWithWitnesses"
            value={formData.interferenceWithWitnesses}
            onChange={handleInputChange}
            required
          >
            <option value="No">No</option>
            <option value="Possible">Possible</option>
            <option value="Likely">Likely</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📝 Legal Arguments & Declaration
      </div>
      
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Legal Arguments</label>
          <textarea
            name="legalArguments"
            value={formData.legalArguments}
            onChange={handleInputChange}
            rows="4"
            placeholder="Legal arguments and precedents supporting the bail application"
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Case References</label>
          <textarea
            name="caseReferences"
            value={formData.caseReferences}
            onChange={handleInputChange}
            rows="3"
            placeholder="Relevant case law citations and references"
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col">
          <label>Advocate Name</label>
          <input
            type="text"
            name="advocateName"
            value={formData.advocateName}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Advocate Enrollment Number</label>
          <input
            type="text"
            name="advocateEnrollment"
            value={formData.advocateEnrollment}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className="form-page-row">
        <div className="form-page-col full-width">
          <div className="form-page-checkbox">
            <input
              type="checkbox"
              id="declaration"
              name="declaration"
              checked={formData.declaration}
              onChange={handleInputChange}
              required
            />
            <label htmlFor="declaration">
              I declare that the information provided above is true and correct to the best of my knowledge.
              I understand that providing false information may result in legal consequences.
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate('/create-draft')} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">File Bail Application</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional bail application</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}

          {currentStep === totalSteps && (
            <div className="form-page-section">
              <div className="form-page-section-title">📄 Supporting Documents</div>
              <p className="form-page-note">
                Upload FIR, charge sheet, previous bail orders, medical reports, or other supporting documents. Extracted text will be added to your bail application draft.
              </p>
              <DocumentUpload
                onDocumentsChange={handleDocumentsChange}
                maxFiles={8}
                acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
              />
            </div>
          )}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                  disabled={!formData.declaration}
                >
                  Generate Bail Application
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BailFormPage;
